import { EventEmitter } from 'eventemitter3';
import { getLogger } from '@emdzej/bimmerz-logger';
const log = getLogger('INPAX.main-scheduler');
/**
 * Main Scheduler
 *
 * Coordinates execution of Screen, StateMachine, and F-key handlers
 * in the correct priority order:
 *
 * 1. F-key handlers (highest priority - interrupts everything)
 * 2. State machine tick (one state per cycle)
 * 3. Screen LINE execution (continuous when frequentFlag=true)
 *
 * Based on INPA's MFC OnIdle polling model.
 */
export class MainScheduler extends EventEmitter {
    vm;
    // Execution state
    running = false;
    paused = false;
    // Pending F-key action
    pendingMenuAction = null;
    // Configuration
    tickInterval;
    debug;
    // Internal scheduling
    tickTimeout = null;
    lastTickTime = 0;
    constructor(vm, config = {}) {
        super();
        this.vm = vm;
        this.tickInterval = config.tickInterval ?? 16;
        this.debug = config.debug ?? false;
    }
    /**
     * Start the main execution loop
     */
    start() {
        if (this.running) {
            this.log('Already running');
            return;
        }
        this.running = true;
        this.paused = false;
        this.lastTickTime = Date.now();
        this.log('Main scheduler started');
        this.scheduleNextTick();
    }
    /**
     * Stop the main execution loop
     */
    stop() {
        this.running = false;
        this.paused = false;
        if (this.tickTimeout) {
            clearTimeout(this.tickTimeout);
            this.tickTimeout = null;
        }
        // Stop sub-executors
        const screenExecutor = this.vm.getScreenExecutor();
        if (screenExecutor) {
            screenExecutor.stop();
        }
        const smExecutor = this.vm.getStateMachineExecutor();
        if (smExecutor) {
            smExecutor.stop();
        }
        this.log('Main scheduler stopped');
        this.emit('stopped');
    }
    /**
     * Pause execution
     */
    pause() {
        this.paused = true;
        this.log('Main scheduler paused');
    }
    /**
     * Resume execution
     */
    resume() {
        if (this.paused && this.running) {
            this.paused = false;
            this.log('Main scheduler resumed');
            this.scheduleNextTick();
        }
    }
    /**
     * Check if running
     */
    isRunning() {
        return this.running;
    }
    /**
     * Check if paused
     */
    isPaused() {
        return this.paused;
    }
    /**
     * Queue an F-key handler for execution
     * Will be executed on next tick with highest priority
     */
    queueMenuAction(itemNum, handler) {
        this.pendingMenuAction = { itemNum, handler };
        this.log(`Queued menu action: F${itemNum}`);
    }
    /**
     * Check if there's a pending menu action
     */
    hasPendingMenuAction() {
        return this.pendingMenuAction !== null;
    }
    // === Internal Methods ===
    /**
     * Schedule next tick
     */
    scheduleNextTick() {
        if (!this.running || this.paused) {
            return;
        }
        const now = Date.now();
        const elapsed = now - this.lastTickTime;
        const delay = Math.max(0, this.tickInterval - elapsed);
        this.tickTimeout = setTimeout(async () => {
            this.lastTickTime = Date.now();
            await this.tick();
            this.scheduleNextTick();
        }, delay);
    }
    /**
     * Execute one tick of the main loop
     *
     * Priority order:
     * 1. F-key handlers (highest)
     * 2. State machine
     * 3. Screen (lowest)
     */
    async tick() {
        if (!this.running || this.paused) {
            return;
        }
        this.emit('tick:start');
        try {
            // 1. F-key handlers (highest priority). INPA's dispatcher is
            // single-script: the OnIdle screen-refresh loop is paused
            // while a menu item runs, and the handler re-establishes the
            // screen via `setscreen` (which creates a fresh screen
            // executor). We mirror that — stopping the active screen
            // executor before the handler runs prevents its in-flight
            // tick from racing the handler over the shared VM state
            // (`state.currentBlock` / `state.ip`).
            if (this.pendingMenuAction) {
                const action = this.pendingMenuAction;
                this.pendingMenuAction = null;
                const previousScreen = this.vm.getScreenExecutor();
                if (previousScreen?.isRunning()) {
                    previousScreen.stop();
                }
                this.log(`Executing menu action: F${action.itemNum}`);
                await action.handler();
            }
            // 2. State machine tick
            const smExecutor = this.vm.getStateMachineExecutor();
            if (smExecutor?.isRunning() && !smExecutor.isPaused()) {
                this.emit('tick:statemachine');
                await smExecutor.tick();
            }
            // 3. Screen tick (handled internally by ScreenExecutor's own timer)
            // Note: ScreenExecutor runs its own setTimeout loop, so we just
            // emit the event for tracking purposes
            const screenExecutor = this.vm.getScreenExecutor();
            if (screenExecutor?.isRunning() && !screenExecutor.isPaused()) {
                this.emit('tick:screen');
                // ScreenExecutor handles its own timing
            }
            this.emit('tick:complete');
        }
        catch (error) {
            this.log(`Error in tick: ${error}`);
            this.emit('error', error);
        }
    }
    /**
     * Debug logging
     */
    log(message) {
        if (this.debug) {
            log.debug({ message }, 'main scheduler');
        }
    }
}
//# sourceMappingURL=main-scheduler.js.map